import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Mail, MessageSquare, X } from 'lucide-react';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

const STATUS = {
  delivered: { label: 'Delivered', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  queued: { label: 'Queued', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock, title: 'Accepted by the text provider — no delivery receipt yet' },
  failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-200', Icon: AlertTriangle },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function HistoryRow({ item, idx }) {
  const s = STATUS[item.status] || STATUS.queued;
  const isEmail = item.channel === 'email';
  const preview = isEmail ? item.subject : (item.body || '').split('\n')[0];
  return (
    <li className="px-5 py-3 flex gap-3" data-testid={`trainee-message-row-${idx}`}>
      <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isEmail ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>
        {isEmail ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{item.label}</div>
            {preview && <div className="text-xs text-gray-600 truncate" title={isEmail ? item.subject : item.body}>{preview}</div>}
          </div>
          <span title={s.title} className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${s.cls}`} data-testid={`trainee-message-status-${idx}`}>
            <s.Icon className="w-3 h-3" /> {s.label}
          </span>
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5">{isEmail ? 'Email' : 'Text'} · {fmt(item.at)} · to {item.to}</div>
        {item.status === 'failed' && item.error && <div className="text-[11px] text-red-700 mt-0.5 break-words">{item.error}</div>}
      </div>
    </li>
  );
}

/** Staff: everything a trainee has been sent (emails + texts) with delivery status. */
export function TraineeMessageHistoryDialog({ reg, courseId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!reg) return;
    setData(null); setError('');
    fetch(`${API_URL}/api/staff/training-courses/${courseId}/registrations/${reg.id}/messages`, { credentials: 'include' })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(formatApiError(d) || 'Could not load history'); setData(d); })
      .catch((e) => setError(e.message));
  }, [reg, courseId]);

  if (!reg) return null;
  const counts = data?.counts;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose} data-testid="trainee-message-history-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-gray-500" /> Messages sent to {reg.name}</h2>
            {counts && (
              <p className="text-xs text-gray-500 mt-0.5" data-testid="trainee-message-counts">
                {counts.sms} text{counts.sms === 1 ? '' : 's'} · {counts.email} email{counts.email === 1 ? '' : 's'}{counts.failed ? <span className="text-red-600"> · {counts.failed} failed</span> : ''}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Close" data-testid="trainee-message-history-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1">
          {!data && !error && <div className="py-10 flex justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {error && <div className="px-5 py-6 text-sm text-red-700" data-testid="trainee-message-history-error">{error}</div>}
          {data && data.items.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-gray-500" data-testid="trainee-message-history-empty">
              Nothing sent yet{!data.trainee.email && !data.trainee.phone ? ' — no email or mobile on file.' : '.'}
            </div>
          )}
          {data && data.items.length > 0 && <ul className="divide-y">{data.items.map((it, i) => <HistoryRow key={i} item={it} idx={i} />)}</ul>}
        </div>
      </div>
    </div>
  );
}
